export interface UsageReporterOptions {
  key: string;
  domain: string;
  /** Empty string = dev mode: report nothing. */
  licenseServer: string;
  flushIntervalMs?: number;
  /** Injectable transport; defaults to navigator.sendBeacon with fetch fallback. */
  sendFn?: (url: string, body: string) => void;
  /** Anonymous per-browser id (random UUID, no fingerprinting). */
  deviceId?: string;
  platform?: string;
  model?: string;
}

/**
 * Batches scan counts and reports them per protocol v1. Fire-and-forget:
 * telemetry must never affect scanning.
 */
export class UsageReporter {
  private pendingScans = 0;
  private pendingSymbologies = new Map<string, number>();
  private sessionReported = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private readonly flushIntervalMs: number;
  private readonly sendFn: (url: string, body: string) => void;
  private readonly onHide = () => {
    if (document.visibilityState === "hidden") this.flush();
  };

  constructor(private readonly opts: UsageReporterOptions) {
    this.flushIntervalMs = opts.flushIntervalMs ?? 60_000;
    this.sendFn = opts.sendFn ?? defaultSend;
  }

  countScan(n = 1, symbology?: string): void {
    this.pendingScans += n;
    if (symbology) {
      this.pendingSymbologies.set(
        symbology,
        (this.pendingSymbologies.get(symbology) ?? 0) + n,
      );
    }
    this.scheduleFlush();
  }

  /**
   * Flush a couple seconds after a scan burst so counts land *during* the
   * session. Otherwise short visits (scan, then close within the 60s heartbeat)
   * only had the teardown beacon to carry scans — easily lost — so sessions got
   * recorded but scans read as 0.
   */
  private scheduleFlush(): void {
    if (this.stopped || !this.opts.licenseServer || this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, 2500);
  }

  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => this.flush(), this.flushIntervalMs);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.onHide);
    }
  }

  flush(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (!this.opts.licenseServer) return;
    const sessions = this.sessionReported ? 0 : 1;
    if (this.pendingScans === 0 && sessions === 0) return;
    const payload: Record<string, unknown> = {
      key: this.opts.key,
      domain: this.opts.domain,
      scans: this.pendingScans,
      sessions,
    };
    if (this.opts.deviceId) payload.deviceId = this.opts.deviceId;
    if (this.opts.platform) payload.platform = this.opts.platform;
    if (this.opts.model) payload.model = this.opts.model;
    if (this.pendingSymbologies.size > 0) {
      payload.symbologies = Object.fromEntries(this.pendingSymbologies);
    }
    const body = JSON.stringify(payload);
    this.pendingScans = 0;
    this.pendingSymbologies.clear();
    this.sessionReported = true;
    try {
      this.sendFn(`${this.opts.licenseServer}/api/v1/license/usage`, body);
    } catch {
      // Telemetry is best-effort by design.
    }
  }

  stop(): void {
    // Final flush for scans counted in the last debounce window, so they aren't
    // lost on teardown/replace. Skip when nothing is pending — no bare sessions.
    if (this.pendingScans > 0) this.flush();
    this.stopped = true;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.onHide);
    }
  }
}

let activeReporter: UsageReporter | null = null;

/** Module-level registry so ScannerView can count scans without a cycle. */
export function setActiveReporter(reporter: UsageReporter | null): void {
  activeReporter?.stop();
  activeReporter = reporter;
}

export function getActiveReporter(): UsageReporter | null {
  return activeReporter;
}

function defaultSend(url: string, body: string): void {
  if (typeof navigator !== "undefined" && "sendBeacon" in navigator) {
    navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    return;
  }
  void fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => undefined);
}
